import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Drawer,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  IconButton,
  Tooltip,
  useMediaQuery,
  Collapse,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CustomLabel from "./CustomLabel";
import PinAccessDialog from "./PinAccessDialog";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { COLORS } from "../assets/theme";
import { getCachedPinAccess } from "../utils/pinAccessCache";

interface MenuItem {
  label: string;
  icon: React.ReactNode;
  to?: string;
  children?: MenuItem[];
}

interface SidebarProps {
  mobileOpen: boolean;
  onDrawerToggle: () => void;
  sidebarCollapsed: boolean;
  onSidebarToggle: () => void;
  drawerWidth: number;
  collapsedWidth: number;
  menuItems: MenuItem[];
  venueName: string;
}

const Sidebar: React.FC<SidebarProps> = ({
  mobileOpen,
  onDrawerToggle,
  sidebarCollapsed,
  onSidebarToggle,
  drawerWidth,
  collapsedWidth,
  menuItems,
  venueName,
}) => {
  const location = useLocation();
  const theme = useTheme();
  const isSmUp = useMediaQuery(theme.breakpoints.up("sm"));
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>(
    {}
  );
  const financeMenuLabel = "Finance";
  const [pinDialogOpen, setPinDialogOpen] = useState(false);
  const normalizePath = (path: string) => {
    const trimmed = path.replace(/\/+$/, "");
    return trimmed.length ? trimmed : "/";
  };
  const menuPaths = useMemo(() => {
    const paths: string[] = [];
    const collectPaths = (items: MenuItem[]) => {
      items.forEach((menuItem) => {
        if (menuItem.to) {
          paths.push(normalizePath(menuItem.to));
        }
        if (menuItem.children) {
          collectPaths(menuItem.children);
        }
      });
    };
    collectPaths(menuItems);
    return paths;
  }, [menuItems]);
  const normalizedCurrentPath = normalizePath(location.pathname);
  const isFinanceRoute = normalizedCurrentPath.includes("/finance/");

  useEffect(() => {
    if (isFinanceRoute) return;
    setExpandedItems((prev) =>
      prev[financeMenuLabel] ? { ...prev, [financeMenuLabel]: false } : prev
    );
  }, [financeMenuLabel, isFinanceRoute]);

  const handleOpenFinancePinDialog = () => {
    if (pinDialogOpen) return;
    setPinDialogOpen(true);
  };

  const handleCloseFinancePinDialog = () => {
    setPinDialogOpen(false);
  };

  const handleFinancePinVerified = () => {
    setPinDialogOpen(false);
    handleToggleExpand(financeMenuLabel);
  };

  const handleToggleExpand = (itemLabel: string) => {
    setExpandedItems((prev) => {
      const isCurrentlyExpanded = prev[itemLabel];

      if (isCurrentlyExpanded) {
        return {
          ...prev,
          [itemLabel]: false,
        };
      } else {
        const newState: Record<string, boolean> = {};
        Object.keys(prev).forEach((key) => {
          newState[key] = false;
        });
        newState[itemLabel] = true;
        return newState;
      }
    });
  };

  const handleParentItemClick = (item: MenuItem) => {
    if (item.label === financeMenuLabel) {
      const cachedAccess = getCachedPinAccess();
      if (!cachedAccess) {
        handleOpenFinancePinDialog();
        return;
      }
      handleToggleExpand(financeMenuLabel);
      return;
    }
    handleToggleExpand(item.label);
  };

  const isItemActive = (item: MenuItem): boolean => {
    if (item.to) {
      const normalizedItemPath = normalizePath(item.to);
      const hasDescendantPath = menuPaths.some((path) => {
        if (path === normalizedItemPath) {
          return false;
        }
        return path.startsWith(`${normalizedItemPath}/`);
      });

      if (normalizedCurrentPath === normalizedItemPath) {
        return true;
      }

      if (
        !hasDescendantPath &&
        normalizedCurrentPath.startsWith(`${normalizedItemPath}/`)
      ) {
        return true;
      }
    }

    if (item.children) {
      return item.children.some((child) => isItemActive(child));
    }

    return false;
  };

  const renderMenuItem = (item: MenuItem, level: number = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems[item.label];
    const itemIsActive = isItemActive(item);

    return (
      <React.Fragment key={item.label}>
        <ListItem disablePadding sx={{ mb: { xs: 1, sm: 0.5 } }}>
          <Tooltip
            title={sidebarCollapsed && isSmUp ? item.label : ""}
            placement="right"
          >
            <ListItemButton
              component={hasChildren ? "div" : Link}
              to={hasChildren ? undefined : item.to}
              onClick={
                hasChildren ? () => handleParentItemClick(item) : undefined
              }
              sx={{
                borderRadius: 2,
                bgcolor: itemIsActive ? COLORS.SidebarActiveBg : "transparent",
                color: itemIsActive
                  ? COLORS.SidebarActiveText
                  : COLORS.SidebarInactiveText,
                px: sidebarCollapsed && isSmUp ? 1.5 : 2 + level * 0.5,
                py: { xs: 1.2, sm: 0.7 },
                justifyContent:
                  sidebarCollapsed && isSmUp ? "center" : "flex-start",
                minHeight: { xs: 42, sm: 32 },
                transition: "all 0.2s",
                ml: level > 0 ? 1.5 : 0,
                "& .MuiSvgIcon-root": {
                  fontSize:
                    sidebarCollapsed && isSmUp
                      ? "1.5rem"
                      : { xs: "1.4rem", sm: "1.1rem" },
                },
              }}
            >
              {item.icon}
              {(!sidebarCollapsed || !isSmUp) && (
                <>
                  <ListItemText
                    primary={item.label}
                    sx={{
                      ml: 1.5,
                      fontWeight: 600,
                      fontSize:
                        level > 0
                          ? { xs: "0.95rem", sm: "0.8rem" }
                          : { xs: "1.05rem", sm: "0.9rem" },
                      color: itemIsActive
                        ? COLORS.SidebarActiveText
                        : COLORS.SidebarInactiveText,
                    }}
                  />
                  {hasChildren && (
                    <Box sx={{ ml: "auto" }}>
                      {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </Box>
                  )}
                </>
              )}
            </ListItemButton>
          </Tooltip>
        </ListItem>

        {hasChildren && (!sidebarCollapsed || !isSmUp) && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.children!.map((child) => renderMenuItem(child, level + 1))}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  const drawer = (
    <Box
      sx={{
        height: "100%",
        bgcolor: COLORS.WhiteBackground,
        color: COLORS.MainColor,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        pt: { xs: 2, sm: 1.5 },
        mt: { xs: 6, sm: 6 },
      }}
    >
      <List sx={{ width: "100%" }}>
        <ListItem disablePadding sx={{ mb: { xs: 1, sm: 0.5 } }}>
          {sidebarCollapsed && isSmUp ? (
            <ListItemButton
              onClick={onSidebarToggle}
              sx={{
                borderRadius: 2,
                bgcolor: "transparent",
                color: COLORS.MainColor,
                px: { xs: 3, sm: 2 },
                py: { xs: 1.5, sm: 0.7 },
                justifyContent: "center",
                minHeight: { xs: 25, sm: 20 },
                maxHeight: { xs: 35, sm: 28 },
                transition: "all 0.2s",
                mt: { xs: 0, sm: 0.5 },
                "& .MuiSvgIcon-root": {
                  fontSize: { xs: "1.5rem", sm: "1.5rem" },
                },
              }}
            >
              <MenuIcon />
            </ListItemButton>
          ) : (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                px: { xs: 3, sm: 2 },
                py: { xs: 1.5, sm: 1 },
                minHeight: { xs: 25, sm: 20 },
                maxHeight: { xs: 35, sm: 28 },
                justifyContent: { xs: "center", sm: "flex-start" },
              }}
            >
              <Box sx={{ display: { xs: "none", sm: "block" } }}>
                <IconButton
                  onClick={onSidebarToggle}
                  sx={{
                    color: COLORS.MainColor,
                    p: 0,
                    mr: 2,
                    minWidth: "auto",
                  }}
                >
                  <ChevronLeftIcon />
                </IconButton>
              </Box>
              <Tooltip title={venueName || ""} placement="right">
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <CustomLabel
                    text={venueName}
                    variant="h6"
                    fontWeight={700}
                    sx={{
                      color: COLORS.MainColor,
                      fontFamily: "inherit",
                      width: "80%",
                      overflow: "hidden",
                      overflowX: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      minWidth: 0,
                    }}
                  />
                </Box>
              </Tooltip>
            </Box>
          )}
        </ListItem>
        {menuItems.map((item) => renderMenuItem(item))}
      </List>
    </Box>
  );

  return (
    <Box
      component="nav"
      sx={{
        width: { sm: sidebarCollapsed ? collapsedWidth : drawerWidth },
        flexShrink: { sm: 0 },
      }}
      aria-label="sidebar"
    >
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", sm: "none" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: drawerWidth,
            bgcolor: COLORS.WhiteBackground,
            color: COLORS.MainColor,
          },
        }}
      >
        {drawer}
      </Drawer>
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: "none", sm: "block" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: sidebarCollapsed ? collapsedWidth : drawerWidth,
            bgcolor: COLORS.WhiteBackground,
            color: COLORS.MainColor,
            transition: "width 0.2s",
            borderRight: "1px solid #eee",
          },
        }}
        open
      >
        {drawer}
      </Drawer>
      <PinAccessDialog
        open={pinDialogOpen}
        onClose={handleCloseFinancePinDialog}
        onVerified={handleFinancePinVerified}
      />
    </Box>
  );
};

export default Sidebar;
